import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface ExternalObra {
  id: string
  nome: string
  company_id: string
  company_name: string
}

interface ExternalContractorContextType {
  /** Whether the user has any external work allocations */
  hasExternalObras: boolean
  /** List of obras where user is external contractor */
  externalObras: ExternalObra[]
  /** Currently active external mode (null = normal company mode) */
  activeExternalObra: ExternalObra | null
  /** Whether user is in external contractor mode */
  isExternalMode: boolean
  /** Enter external contractor mode for a specific obra */
  enterExternalMode: (obra: ExternalObra) => void
  /** Return to normal company mode */
  exitExternalMode: () => void
}

const ExternalContractorContext = createContext<ExternalContractorContextType | null>(null)

export const useExternalContractor = () => {
  const ctx = useContext(ExternalContractorContext)
  if (!ctx) throw new Error('useExternalContractor must be used within ExternalContractorProvider')
  return ctx
}

/** Menu keys allowed for external contractors */
export const EXTERNAL_CONTRACTOR_MENU_KEYS = new Set([
  'diario', 'fotos', 'ocorrencias', 'pendencias', 'chat', 'documentos', 'assistente-ia',
])

export function ExternalContractorProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [externalObras, setExternalObras] = useState<ExternalObra[]>([])
  const [obrasLoaded, setObrasLoaded] = useState(false)

  // Eagerly restore from localStorage before API loads — avoids flash/reset
  const [activeExternalObra, setActiveExternalObra] = useState<ExternalObra | null>(() => {
    try {
      const saved = localStorage.getItem('cz:external_obra')
      if (saved) return JSON.parse(saved) as ExternalObra
    } catch { /* noop */ }
    return null
  })

  useEffect(() => {
    if (!user?.id) {
      setExternalObras([])
      setObrasLoaded(false)
      setActiveExternalObra(null)
      localStorage.removeItem('cz:external_obra')
      return
    }

    let cancelled = false
    ;(async () => {
      // Single query with join — only external memberships
      const { data: membros } = await supabase
        .from('obra_membros')
        .select('obra_id, obras(id, nome, cidade, status, company_id)')
        .eq('profile_id', user.id)
        .eq('external_user', true)
        .eq('ativo', true)

      if (cancelled) return

      const obras = (membros || [])
        .map((m: any) => m.obras)
        .filter(Boolean)
        // Exclude obras from the user's own company
        .filter((o: any) => o.company_id !== user.company_id)

      if (!obras.length) {
        setExternalObras([])
        setObrasLoaded(true)
        return
      }

      // Get company names
      const companyIds = [...new Set(obras.map((o: any) => o.company_id).filter(Boolean))]
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name')
        .in('id', companyIds)

      const companyMap = new Map((companies || []).map((c: any) => [c.id, c.name]))

      const result: ExternalObra[] = obras.map((o: any) => ({
        id: o.id,
        nome: o.nome,
        company_id: o.company_id,
        company_name: companyMap.get(o.company_id) || 'Empresa',
      }))

      if (!cancelled) {
        setExternalObras(result)
        setObrasLoaded(true)
      }
    })()

    return () => { cancelled = true }
  }, [user?.id, user?.company_id])

  // Validate restored selection once the API list is loaded
  useEffect(() => {
    if (!obrasLoaded) return
    if (!activeExternalObra) return

    // Saved obra no longer in the list — clear it
    if (!externalObras.some(o => o.id === activeExternalObra.id)) {
      setActiveExternalObra(null)
      localStorage.removeItem('cz:external_obra')
    }
  }, [obrasLoaded, externalObras, activeExternalObra])

  const enterExternalMode = useCallback((obra: ExternalObra) => {
    setActiveExternalObra(obra)
    localStorage.setItem('cz:external_obra', JSON.stringify(obra))
  }, [])

  const exitExternalMode = useCallback(() => {
    setActiveExternalObra(null)
    localStorage.removeItem('cz:external_obra')
  }, [])

  return (
    <ExternalContractorContext.Provider value={{
      hasExternalObras: externalObras.length > 0,
      externalObras,
      activeExternalObra,
      isExternalMode: !!activeExternalObra,
      enterExternalMode,
      exitExternalMode,
    }}>
      {children}
    </ExternalContractorContext.Provider>
  )
}
